import { createSlice, createAsyncThunk, PayloadAction, isAnyOf } from "@reduxjs/toolkit";
import httpService from "@shared/services/http.service";
import { API_CONFIG } from "@shared/constants/api";
import { notify } from "@/shared/tostNotification/notify";
import {
  GroupPackageActivity,
  GroupPackageDay,
  GroupPackageDetails,
  GroupPackageListItem,
  GroupPackageStatusCard,
  PackageInclusion,
  PackageExclusion,
  SeasonalPricing,
} from "../../features/groupPackage/types";

interface GroupPackageState {
  packages: GroupPackageListItem[];
  currentPackage: GroupPackageDetails | null;
  days: GroupPackageDay[];
  activeDayId: string | number | null;
  statusCard: GroupPackageStatusCard;
  pagination: {
    currentPage: number;
    totalPages: number;
  };
  loading: boolean;
  daysLoading: boolean;
  uploading: boolean;
  error: string | null;
  ui: {
    isInfoModalOpen: boolean;
    isDayModalOpen: boolean;
    editingDay: GroupPackageDay | null;
    isActivityModalOpen: boolean;
    editingActivity: GroupPackageActivity | null;
    isInclusionsModalOpen: boolean;
    isSpecialNotesModalOpen: boolean;
    isDeleteDayModalOpen: boolean;
    dayIdToDelete: string | number | null;
    activeTab: "days" | "summary" | "terms_conditions" | "privacy_policy" | "seasonal_pricing";
  };
}

interface FetchPackagesParams {
  page?: number;
  searchString?: string;
  filterType?: string;
  status?: string;
  isDeletedUserList?: boolean;
  limit?: number;
  sort_field?: string;
  sort_value?: string;
}

interface CreatePackageParams {
  title?: string;
  destination: string;
  country: string;
  start_date: string;
  end_date: string;
  days?: number;
  nights?: number;
  curatorId: string;
  category_id?: string;
  group_package_image?: File;
}

interface UpdatePackageParams {
  id: string;
  title?: string;
  destination?: string;
  country?: string;
  start_date?: string;
  end_date?: string;
  categoryId?: string;
  days?: number;
  nights?: number;
  group_package_image?: File;
  existing_image?: string;
}

interface UpdateStatusParams {
  id: string;
  status: string;
}

interface DayParams {
  itineraryId: string | number;
  day_number?: number;
  title: string;
  location: string;
  file_name?: string;
}

interface ActivityParams {
  itineraryDayId?: string | number;
  activity_type: string;
  title: string;
  description: string;
  // time: string;
  time?: string;
  hotel_name?: string;
  hotel_address?: string;
  hotel_map_link?: string;
  hotel_phone_no?: string;
  hotel_file_name?: string;
  hotel_rating?: number;
  hotel_days?: number;
  hotel_start_date?: string | Date;
  hotel_end_date?: string | Date;
  hotel_nights?: number;
  hotel_rooms?: number;
  hotel_highlight?: string;
  hotel_highlight_points?: string[];
}

interface OtherDetailsParams {
  id: string;
  special_notes?: string;
  summary?: string;
  terms_conditions?: string;
  privacy_policy?: string;
  package_inclusions?: PackageInclusion[];
  package_exclusions?: PackageExclusion[];
  total_amount?: number;
  package_seasonal_pricing?: SeasonalPricing[];
}

interface UploadResponse {
  filename: string;
  presignedUrl: string;
}

/* -------------------------------------------------------------------------- */
/*                               Initial State                                */
/* -------------------------------------------------------------------------- */

const initialState: GroupPackageState = {
  packages: [],
  currentPackage: null,
  days: [],
  activeDayId: null,
  statusCard: {
    total: 0,
    accepted: 0,
    paid: 0,
    pending: 0,
    reOpen: 0,
  },
  pagination: {
    currentPage: 1,
    totalPages: 1,
  },
  loading: false,
  daysLoading: false,
  uploading: false,
  error: null,
  ui: {
    isInfoModalOpen: false,
    isDayModalOpen: false,
    editingDay: null,
    isActivityModalOpen: false,
    editingActivity: null,
    isInclusionsModalOpen: false,
    isSpecialNotesModalOpen: false,
    isDeleteDayModalOpen: false,
    dayIdToDelete: null,
    activeTab: "days",
  },
};

/* -------------------------------------------------------------------------- */
/*                              Helper Functions                              */
/* -------------------------------------------------------------------------- */

const normalizeStatus = (value: string) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-+/g, "_");

const canEditByStatus = (value: string) => {
  const status = normalizeStatus(value);
  const locked = new Set(["paid", "booking_process", "complete"]);
  if (locked.has(status)) return false;
  return true;
};

const normalizeHotelRating = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const formatStatusOptions = (options: any): { value: string; label: string }[] => {
  const statusLabelMap: Record<string, string> = {
    pending: "Pending",
    rejected: "Rejected",
    accepted: "Accepted",
    draft: "Draft",
    paid: "Paid",
    booking_process: "Booking Process",
    complete: "Complete",
    re_open: "Re-Open",
  };

  const getStatusLabel = (value: string) =>
    statusLabelMap[value] ??
    value
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");

  return (Array.isArray(options) ? options : [])
    .map((option) => {
      if (typeof option === "string") {
        return { value: option, label: getStatusLabel(option) };
      }
      if (option && typeof option === "object") {
        const value = option.value ?? option.status ?? option.name;
        if (!value) return null;
        return { value, label: option.label ?? getStatusLabel(value) };
      }
      return null;
    })
    .filter(Boolean) as { value: string; label: string }[];
};

const formatPackageFromApi = (item: any): GroupPackageListItem => ({
  id: item.id,
  title: item.title,
  record_id: item.record_id,
  destination: item.destination,
  country: item.country,
  destinations: item.destination,
  days: item.days,
  nights: item.nights,
  curator: item.curator || "-",
  customer: item.customer,
  status: item.status,
  canEdit: canEditByStatus(item.status),
  amount: item.total_amount ? item.total_amount : "-",
  statusOptions: formatStatusOptions(JSON.parse(item.statusOptions || "[]")),
  package_inclusions: item.package_inclusions,
  package_seasonal_pricing: item.package_seasonal_pricing,
  createdAt: item.createdAt,
  start_date: item.start_date,
  end_date: item.end_date,
});
const formatDayFromApi = (item: any): GroupPackageDay => ({
  id: item.id,
  name: item.day_number,
  location: item.location,
  file_name: item.file_name,
  file_url: item.file_url,
  title: item.title,
  activities: (item.activities ?? []).map((act: any) => ({
    id: act.id,
    activity_type: act.activity_type ?? act.type ?? "",
    type: act.activity_type ?? act.type ?? "",
    title: act.title ?? "",
    description: act.description ?? "",
    time: act.time ?? "",
    hotel_name: act.hotel_name ?? "",
    hotel_address: act.hotel_address ?? "",
    hotel_phone_no: act.hotel_phone_no ?? "",
    hotel_map_link: act.hotel_map_link ?? "",
    hotel_file_name: act.hotel_file_name ?? "",
    hotel_rating: normalizeHotelRating(act.hotel_rating),
    hotel_image_url: act.hotel_image_url ?? "",
    hotel_days: act.hotel_days,
    hotel_start_date: act.hotel_start_date ?? "",
    hotel_end_date: act.hotel_end_date ?? "",
    hotel_nights: act.hotel_nights,
    hotel_rooms: act.hotel_rooms,
    hotel_highlight: Array.isArray(act.hotel_highlight)
      ? act.hotel_highlight.join(", ")
      : (act.hotel_highlight ?? ""),
    hotel_highlight_points: act.hotel_highlight_points ?? [],
    itineraryDayId: act.itineraryDayId,
  })),
});

/* -------------------------------------------------------------------------- */
/*                               Async Thunks                                 */
/* -------------------------------------------------------------------------- */

/**
 * Fetch all group packages with pagination and filters
 * GET: /group-packages/all
 */
export const fetchGroupPackages = createAsyncThunk<
  { packages: GroupPackageListItem[]; pagination: any; statusCard: GroupPackageStatusCard },
  FetchPackagesParams
>("groupPackage/fetchAll", async (params, { rejectWithValue }) => {
  try {
    const response = await httpService.get(API_CONFIG.path.group_package_all, params);
    const list = Array.isArray(response.data) ? response.data : [];
    const packages = list.map(formatPackageFromApi);
    const pagination = response?.pagination ?? {};

    return {
      packages,
      pagination: {
        currentPage: params.page ?? 1,
        totalPages: pagination.totalPages ?? 1,
      },
      statusCard: {
        total: pagination.totalItineraries ?? 0,
        accepted: pagination.acceptedItineraries ?? 0,
        paid: pagination.paidItineraries ?? 0,
        pending: pagination.pendingItineraries ?? 0,
        reOpen: pagination.reOpenItineraries ?? 0,
      },
    };
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch group packages");
  }
});

/**
 * Fetch single group package details
 * GET: /group-packages/:id
 */
export const fetchGroupPackage = createAsyncThunk<GroupPackageDetails, string | number>(
  "groupPackage/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      const response = await httpService.get(`${API_CONFIG.path.group_package_get}/${id}`);
      const details = response.data;
      return {
        id: details?.id,
        title: details?.title,
        record_id: details?.record_id,
        status: details.status,
        destination: details?.destination,
        country: details?.country,
        startDate: details?.start_date,
        endDate: details?.end_date,
        duration: details?.duration
          ? { day: details.duration.days, night: details.duration.nights }
          : undefined,
        customerId: details?.customerId,
        customer: details?.customer,
        curatorId: details?.curatorId,
        curator: details?.curator,
        category_id: details?.category_id,
        category: details?.category,
        group_package_image: details?.group_package_image,
        group_package_image_url: details?.group_package_image_url,
        special_notes: details?.special_notes,
        summary: details?.summary,
        terms_conditions: details?.terms_conditions,
        privacy_policy: details?.privacy_policy,
        package_inclusions: details?.package_inclusions,
        package_exclusions: details?.package_exclusions,
        total_amount: details?.total_amount,
        days: details?.days,
        nights: details?.nights,
        package_seasonal_pricing: details?.package_seasonal_pricing || [],
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch group package");
    }
  }
);

/**
 * Upload group package image
 * POST: /file-upload/single
 */
export const uploadGroupPackageImage = createAsyncThunk<UploadResponse, File>(
  "groupPackage/uploadImage",
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await httpService.post(API_CONFIG.path.image_upload, formData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to upload image");
    }
  }
);

/**
 * Create new group package
 * POST: /group-packages/create
 */
export const createGroupPackage = createAsyncThunk<any, CreatePackageParams>(
  "groupPackage/create",
  async (params, { dispatch, rejectWithValue }) => {
    try {
      let imageFilename: string | undefined;

      // Upload image if provided
      if (params.group_package_image) {
        const uploadResult = await dispatch(
          uploadGroupPackageImage(params.group_package_image)
        ).unwrap();
        imageFilename = uploadResult.filename;
      }

      // Create package with image filename
      const { group_package_image, ...createParams } = params;
      const response = await httpService.post(API_CONFIG.path.group_package_create, {
        ...createParams,
        ...(imageFilename && { group_package_image: imageFilename }),
      });
      notify(response.message || "Group package created", "success");
      return response.data;
    } catch (error: any) {
      notify(error.message || "Failed to create group package", "error");
      return rejectWithValue(error.message || "Failed to create group package");
    }
  }
);

/**
 * Update group package
 * PUT: /group-packages/update/:id
 */
export const updateGroupPackage = createAsyncThunk<any, UpdatePackageParams>(
  "groupPackage/update",
  async ({ id, group_package_image, existing_image, ...params }, { dispatch, rejectWithValue }) => {
    try {
      let imageFilename = existing_image || "";

      // Upload new image if provided
      if (group_package_image) {
        const uploadResult = await dispatch(uploadGroupPackageImage(group_package_image)).unwrap();
        imageFilename = uploadResult.filename;
      }

      const response = await httpService.put(`${API_CONFIG.path.group_package_update}/${id}`, {
        ...params,
        ...(imageFilename && { group_package_image: imageFilename }),
      });
      notify(response.message || "Group package updated", "success");
      return response.data;
    } catch (error: any) {
      notify(error.message || "Failed to update group package", "error");
      return rejectWithValue(error.message || "Failed to update group package");
    }
  }
);

/**
 * Update group package status
 * PUT: /group-packages/update-status/:id
 */
export const updatePackageStatus = createAsyncThunk<
  { id: string; status: string },
  UpdateStatusParams
>("groupPackage/updateStatus", async ({ id, status }, { rejectWithValue }) => {
  try {
    const response = await httpService.put(`${API_CONFIG.path.group_package_update_status}/${id}`, {
      status,
    });
    notify(response.message || "Status updated", "success");
    return { id, status };
  } catch (error: any) {
    notify(error.message || "Failed to update status", "error");
    return rejectWithValue(error.message || "Failed to update status");
  }
});

/**
 * Delete group packages (soft delete)
 * DELETE: /group-packages/delete
 */
export const deleteGroupPackages = createAsyncThunk<string[], string[]>(
  "groupPackage/delete",
  async (ids, { rejectWithValue }) => {
    try {
      const response = await httpService.deleteRequest(API_CONFIG.path.group_package_delete, {
        ids,
        is_delete: "soft",
      });
      notify(response.message || "Moved to trash", "success");
      return ids;
    } catch (error: any) {
      notify(error.message || "Failed to delete", "error");
      return rejectWithValue(error.message || "Failed to delete");
    }
  }
);

/**
 * Restore group packages
 * POST: /group-packages/restore
 */
export const restoreGroupPackages = createAsyncThunk<string[], string[]>(
  "groupPackage/restore",
  async (ids, { rejectWithValue }) => {
    try {
      const response = await httpService.post(API_CONFIG.path.group_package_restore, { ids });
      notify(response.message || "Restored successfully", "success");
      return ids;
    } catch (error: any) {
      notify(error.message || "Failed to restore", "error");
      return rejectWithValue(error.message || "Failed to restore");
    }
  }
);

/**
 * Fetch days for a group package
 * GET: /group-packages/day/by-group-package/:id
 */
export const fetchDays = createAsyncThunk<GroupPackageDay[], string | number>(
  "groupPackage/fetchDays",
  async (packageId, { rejectWithValue }) => {
    try {
      const response = await httpService.get(`${API_CONFIG.path.group_package_day}/${packageId}`);
      const list = Array.isArray(response.data) ? response.data : [];
      return list.map(formatDayFromApi);
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch days");
    }
  }
);

/**
 * Create day
 * POST: /group-packages/day/create
 */
export const createDay = createAsyncThunk<GroupPackageDay, DayParams>(
  "groupPackage/createDay",
  async (params, { rejectWithValue }) => {
    try {
      const response = await httpService.post(API_CONFIG.path.group_package_day_create, params);
      notify(response.message || "Day added", "success");
      return formatDayFromApi(response.data);
    } catch (error: any) {
      notify(error.message || "Failed to add day", "error");
      return rejectWithValue(error.message || "Failed to add day");
    }
  }
);

/**
 * Update day
 * PUT: /group-packages/day/update/:id
 */
export const updateDay = createAsyncThunk<GroupPackageDay, { id: number } & Partial<DayParams>>(
  "groupPackage/updateDay",
  async ({ id, ...params }, { rejectWithValue }) => {
    try {
      const response = await httpService.put(
        `${API_CONFIG.path.group_package_day_update}/${id}`,
        params
      );
      notify(response.message || "Day updated", "success");
      return formatDayFromApi(response.data);
    } catch (error: any) {
      notify(error.message || "Failed to update day", "error");
      return rejectWithValue(error.message || "Failed to update day");
    }
  }
);

/**
 * Delete day
 * DELETE: /group-packages/day/delete/:id
 */
export const deleteDay = createAsyncThunk<string | number, string | number>(
  "groupPackage/deleteDay",
  async (dayId, { rejectWithValue }) => {
    try {
      const response = await httpService.deleteRequest(
        `${API_CONFIG.path.group_package_day_delete}/${dayId}`
      );
      notify(response.message || "Day deleted", "success");
      return dayId;
    } catch (error: any) {
      notify(error.message || "Failed to delete day", "error");
      return rejectWithValue(error.message || "Failed to delete day");
    }
  }
);

/**
 * Reorder days
 * PUT: /group-packages/day/bulk-update/:packageId
 */
export const reorderDays = createAsyncThunk<
  void,
  { packageId: number | string; days: { id: number | string; day_number: number }[] }
>("groupPackage/reorderDays", async ({ packageId, days }, { rejectWithValue }) => {
  try {
    const response = await httpService.put(
      `${API_CONFIG.path.group_package_day_reorder}/${packageId}`,
      { days }
    );
    notify(response.message || "Days reordered", "success");
  } catch (error: any) {
    notify(error.message || "Failed to reorder days", "error");
    return rejectWithValue(error.message || "Failed to reorder days");
  }
});

/**
 * Create activity
 * POST: /group-packages/activity/create
 */
export const createActivity = createAsyncThunk<GroupPackageActivity, ActivityParams>(
  "groupPackage/createActivity",
  async (params, { rejectWithValue }) => {
    try {
      const response = await httpService.post(
        API_CONFIG.path.group_package_activity_create,
        params
      );
      return response.data;
    } catch (error: any) {
      notify(error.message || "Failed to add activity", "error");
      return rejectWithValue(error.message || "Failed to add activity");
    }
  }
);

/**
 * Update activity
 * PUT: /group-packages/activity/update/:id
 */
export const updateActivity = createAsyncThunk<
  GroupPackageActivity,
  { id: number | string } & Partial<ActivityParams>
>("groupPackage/updateActivity", async ({ id, ...params }, { rejectWithValue }) => {
  try {
    const response = await httpService.put(
      `${API_CONFIG.path.group_package_activity_update}/${id}`,
      params
    );
    return response.data;
  } catch (error: any) {
    notify(error.message || "Failed to update activity", "error");
    return rejectWithValue(error.message || "Failed to update activity");
  }
});

/**
 * Delete activity
 * DELETE: /group-packages/activity/delete/:id
 */
export const deleteActivity = createAsyncThunk<string | number, string | number>(
  "groupPackage/deleteActivity",
  async (id, { rejectWithValue }) => {
    try {
      await httpService.deleteRequest(`${API_CONFIG.path.group_package_activity_delete}/${id}`);
      return id;
    } catch (error: any) {
      notify(error.message || "Failed to delete activity", "error");
      return rejectWithValue(error.message || "Failed to delete activity");
    }
  }
);

/**
 * Update package settings (inclusions, exclusions, notes)
 * PUT: /group-packages/other-details/:id
 */
export const updatePackageSettings = createAsyncThunk<any, OtherDetailsParams>(
  "groupPackage/updatePackageSettings",
  async ({ id, ...params }, { rejectWithValue }) => {
    try {
      const response = await httpService.put(
        `${API_CONFIG.path.group_package_other_details}/${id}`,
        params
      );
      notify(response.message || "Settings updated", "success");
      return response.data;
    } catch (error: any) {
      notify(error.message || "Failed to update settings", "error");
      return rejectWithValue(error.message || "Failed to update settings");
    }
  }
);

/* -------------------------------------------------------------------------- */
/*                                  Slice                                     */
/* -------------------------------------------------------------------------- */

const groupPackageSlice = createSlice({
  name: "groupPackage",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetCurrentPackage: (state) => {
      state.currentPackage = null;
      state.days = [];
      state.activeDayId = null;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    updatePackageInList: (state, action: PayloadAction<{ id: string; status: string }>) => {
      const { id, status } = action.payload;
      const pkg = state.packages.find((p) => p.id === id);
      if (pkg) {
        pkg.status = status;
        pkg.canEdit = canEditByStatus(status);
      }
    },
    setDays: (state, action: PayloadAction<GroupPackageDay[]>) => {
      state.days = action.payload;
    },
    setActiveDayId: (state, action: PayloadAction<string | number | null>) => {
      state.activeDayId = action.payload;
    },
    // UI Reducers
    openInfoModal: (state) => {
      state.ui.isInfoModalOpen = true;
    },
    closeInfoModal: (state) => {
      state.ui.isInfoModalOpen = false;
    },
    openDayModal: (state, action: PayloadAction<GroupPackageDay | null>) => {
      state.ui.isDayModalOpen = true;
      state.ui.editingDay = action.payload;
    },
    closeDayModal: (state) => {
      state.ui.isDayModalOpen = false;
      state.ui.editingDay = null;
    },
    openActivityModal: (state, action: PayloadAction<GroupPackageActivity | null>) => {
      state.ui.isActivityModalOpen = true;
      state.ui.editingActivity = action.payload;
    },
    closeActivityModal: (state) => {
      state.ui.isActivityModalOpen = false;
      state.ui.editingActivity = null;
    },
    openInclusionsModal: (state) => {
      state.ui.isInclusionsModalOpen = true;
    },
    closeInclusionsModal: (state) => {
      state.ui.isInclusionsModalOpen = false;
    },
    openSpecialNotesModal: (state) => {
      state.ui.isSpecialNotesModalOpen = true;
    },
    closeSpecialNotesModal: (state) => {
      state.ui.isSpecialNotesModalOpen = false;
    },
    openDeleteDayModal: (state, action: PayloadAction<string | number>) => {
      state.ui.isDeleteDayModalOpen = true;
      state.ui.dayIdToDelete = action.payload;
    },
    closeDeleteDayModal: (state) => {
      state.ui.isDeleteDayModalOpen = false;
      state.ui.dayIdToDelete = null;
    },
    setActiveTab: (state, action: PayloadAction<GroupPackageState["ui"]["activeTab"]>) => {
      state.ui.activeTab = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      // Fetch all packages
      .addCase(fetchGroupPackages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupPackages.fulfilled, (state, action) => {
        state.loading = false;
        state.packages = action.payload.packages;
        state.pagination = action.payload.pagination;
        state.statusCard = action.payload.statusCard;
      })
      .addCase(fetchGroupPackages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch single package
      .addCase(fetchGroupPackage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupPackage.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPackage = action.payload;
        // Set days when fetching a single package
        const daysArray = Array.isArray(action.payload.days) ? action.payload.days : [];
        state.days = daysArray.map(formatDayFromApi);
        if (!state.activeDayId && state.days.length > 0) {
          state.activeDayId = state.days[0].id;
        }
      })
      .addCase(fetchGroupPackage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update status
      .addCase(updatePackageStatus.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        const pkg = state.packages.find((p) => p.id === id);
        if (pkg) {
          pkg.status = status;
          pkg.canEdit = canEditByStatus(status);
        }
        if (state.currentPackage && state.currentPackage.id === String(id)) {
          state.currentPackage.status = status;
        }
      })

      // Delete packages
      .addCase(deleteGroupPackages.fulfilled, (state, action) => {
        state.packages = state.packages.filter((p) => !action.payload.includes(p.id));
      })

      // Fetch days
      .addCase(fetchDays.pending, (state) => {
        state.daysLoading = true;
      })
      .addCase(fetchDays.fulfilled, (state, action) => {
        state.daysLoading = false;
        state.days = action.payload;
        if (!state.activeDayId && state.days.length > 0) {
          state.activeDayId = state.days[0].id;
        }
      })
      .addCase(fetchDays.rejected, (state) => {
        state.daysLoading = false;
      })

      // Delete day
      .addCase(deleteDay.fulfilled, (state, action) => {
        const deletedId = action.payload;
        state.days = state.days.filter((d) => d.id !== deletedId);
        if (state.activeDayId === deletedId) {
          state.activeDayId = state.days[0]?.id || null;
        }
      })
      // Activity Handlers
      .addCase(createActivity.fulfilled, (state, action) => {
        const newActivity = action.payload;
        const day = state.days.find((d) => d.id === newActivity.itineraryDayId);
        if (day) {
          if (!day.activities) day.activities = [];
          day.activities.push({
            ...newActivity,
            activity_type: newActivity.activity_type ?? newActivity.type ?? "",
            type: newActivity.activity_type ?? newActivity.type ?? "",
          });
        }
        notify("Activity added successfully", "success");
      })
      .addCase(updateActivity.fulfilled, (state, action) => {
        const updatedActivity = action.payload;
        const day = state.days.find((d) => d.id === updatedActivity.itineraryDayId);
        if (day) {
          const index = day.activities.findIndex((a) => a.id === updatedActivity.id);
          if (index !== -1) {
            day.activities[index] = {
              ...updatedActivity,
              activity_type: updatedActivity.activity_type ?? updatedActivity.type ?? "",
              type: updatedActivity.activity_type ?? updatedActivity.type ?? "",
            };
          }
        }
        notify("Activity updated successfully", "success");
      })
      .addCase(deleteActivity.fulfilled, (state, action) => {
        const deletedId = action.payload;
        state.days.forEach((day) => {
          if (day.activities) {
            day.activities = day.activities.filter((a) => a.id !== deletedId);
          }
        });
        notify("Activity deleted successfully", "success");
      })

      // Update package settings
      .addCase(updatePackageSettings.fulfilled, (state, action) => {
        if (state.currentPackage) {
          state.currentPackage = {
            ...state.currentPackage,
            ...action.payload,
          };
        }
      })

      // Upload Group Package Image
      .addCase(uploadGroupPackageImage.pending, (state) => {
        state.uploading = true;
        state.error = null;
      })
      .addCase(uploadGroupPackageImage.fulfilled, (state) => {
        state.uploading = false;
      })
      .addCase(uploadGroupPackageImage.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload as string;
      })
      // Create/Update package success updates currentPackage state
      .addMatcher(
        isAnyOf(createGroupPackage.fulfilled, updateGroupPackage.fulfilled),
        (state, action) => {
          const data = action.payload;
          const daysArray = Array.isArray(data.days) ? data.days : [];
          const daysCount = Array.isArray(data.days) ? data.days.length : data.days || 0;
          const nightsCount = data.nights ?? (daysCount > 0 ? daysCount - 1 : 0);

          state.currentPackage = {
            id: data.id,
            record_id: data.record_id,
            status: data.status,
            destination: data.destination,
            country: data.country,
            startDate: data.start_date ?? null,
            endDate: data.end_date ?? null,
            duration: { day: daysCount, night: nightsCount },
            customerId: data.customerId,
            customer: data.customer,
            category_id: data.category_id,
            category: data.category,
            group_package_image: data.group_package_image,
            group_package_image_url: data.group_package_image_url,
            package_inclusions: data.package_inclusions,
            package_exclusions: data.package_exclusions,
            special_notes: data.special_notes,
            summary: data.summary,
            terms_conditions: data.terms_conditions,
            privacy_policy: data.privacy_policy,
          };
          state.days = daysArray.map(formatDayFromApi);
        }
      );
  },
});

/* -------------------------------------------------------------------------- */
/*                                  Exports                                   */
/* -------------------------------------------------------------------------- */

export const {
  clearError,
  resetCurrentPackage,
  setCurrentPage,
  updatePackageInList,
  setDays,
  setActiveDayId,
  setActiveTab,
  openInfoModal,
  closeInfoModal,
  openDayModal,
  closeDayModal,
  openActivityModal,
  closeActivityModal,
  openInclusionsModal,
  closeInclusionsModal,
  openSpecialNotesModal,
  closeSpecialNotesModal,
  openDeleteDayModal,
  closeDeleteDayModal,
} = groupPackageSlice.actions;

export default groupPackageSlice.reducer;
